import type {
  PluginCatalogueItem,
  PluginMemberRequest,
  PluginScope,
} from '@/shared/api/types/plugins';
import { PluginPropertyPicker } from './PluginPropertyPicker';
import { pluginActionLabel } from './pluginPresentation';

export function PluginMemberFields({
  plugin,
  draft,
  busy,
  change,
}: {
  plugin: PluginCatalogueItem;
  draft: PluginMemberRequest;
  busy: boolean;
  change: (next: Partial<PluginMemberRequest>) => void;
}) {
  const setScope = (scope: PluginScope) =>
    change(
      scope === 'None'
        ? { scope, propertyIds: [], actions: [], guestCommunicationPii: false }
        : {
            scope,
            propertyIds: scope === 'Selected' ? draft.propertyIds : [],
            actions: draft.actions.filter(
              (action) => scope === 'TenantAll' || action !== 'communication.settings.manage',
            ),
          },
    );
  return (
    <>
      <fieldset disabled={busy} className="space-y-1">
        <legend className="mb-2 text-sm font-semibold">Apartmentumfang</legend>
        {(['None', 'Selected', 'TenantAll'] as const).map((scope) => (
          <label key={scope} className="flex min-h-11 cursor-pointer items-center gap-3 text-sm">
            <input
              type="radio"
              name="plugin-member-scope"
              checked={draft.scope === scope}
              onChange={() => setScope(scope)}
              className="accent-sage h-5 w-5"
            />
            {
              {
                None: 'Kein Zugriff',
                Selected: 'Ausgewählte Apartments',
                TenantAll: 'Alle aktuellen und künftigen Apartments',
              }[scope]
            }
          </label>
        ))}
      </fieldset>
      {draft.scope === 'Selected' && (
        <PluginPropertyPicker
          value={draft.propertyIds}
          onChange={(propertyIds) => change({ propertyIds })}
        />
      )}
      {draft.scope !== 'None' && (
        <fieldset disabled={busy}>
          <legend className="text-sm font-semibold">Erlaubte Aktionen</legend>
          {plugin.memberActions.map((action) => (
            <label key={action} className="flex min-h-12 items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={draft.actions.includes(action)}
                className="accent-sage h-5 w-5 shrink-0"
                disabled={action === 'communication.settings.manage' && draft.scope !== 'TenantAll'}
                onChange={(e) =>
                  change({
                    actions: e.target.checked
                      ? [...draft.actions, action]
                      : draft.actions.filter((x) => x !== action),
                  })
                }
              />
              <span>
                {pluginActionLabel(action)}
                {action === 'communication.settings.manage' && (
                  <span className="text-ink-soft block text-xs">
                    Benötigt alle Apartments; verwaltet den Sprachstil der gesamten Verbindung.
                  </span>
                )}
              </span>
            </label>
          ))}
          {plugin.canGrantPii && plugin.installation?.guestCommunicationPii && (
            <label className="flex min-h-12 items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={draft.guestCommunicationPii}
                onChange={(e) => change({ guestCommunicationPii: e.target.checked })}
                className="accent-sage h-5 w-5 shrink-0"
              />
              Personenbezogene Gästekommunikation lesen
            </label>
          )}
        </fieldset>
      )}
    </>
  );
}
